/**
 * Activity 1 General Form.
 *
 * Handles:
 *  - Date picker constraints to the selected site year
 *  - Plants in bloom copy button (AJAX)
 *  - Custom submit buttons (JS-injected, replacing AF's native submit)
 *  - Dynamic button set based on data type radio selection and edit mode
 *  - Convert 1A → 1C notice + button (edit mode, summary-type records only)
 *  - Read-only treatment of summary section for 1C (detail) records in edit mode
 */
const { constrain_date_pickers } = require('../helpers/_utils');

module.exports = function init_activity1_form($) {

  if (!window.acf) return;

  const settings  = (typeof mlmp_settings !== 'undefined') ? mlmp_settings : {};
  const a1        = settings.activity_1 || {};
  const ctx       = settings.a1_form    || {};
  const is_edit   = !!ctx.is_edit;
  const rec_type  = ctx.record_type || ''; // 'summary', 'detail', or ''

  // ── Time pickers: disable seconds selector ─────────────────────────────────
  // ACF calls applyFilters('time_picker_args') before acf.newTimePicker(), so
  // this filter runs before the widget is created — no timing issues.
  acf.addFilter('time_picker_args', function (args) {
    args.showSecond = false;
    return args;
  });

  acf.addAction('ready', function () {

    const $form = $('.af-form');
    if (!$form.length) return;

    // ── Date picker: constrain to site year ──────────────────────────────────
    constrain_date_pickers($form, settings.site_year);

    // ── Plants in bloom copy ─────────────────────────────────────────────────
    $(document).on('click', '#a1-copy-plants-in-bloom', function (e) {
      e.preventDefault();
      const $btn      = $(this);
      const $textarea = $btn.closest('.acf-field[data-name="plants_in_bloom"]').find('textarea');

      let $note = $btn.next('.a1-plants-note');
      if (!$note.length) {
        $note = $('<small>', { class: 'a1-plants-note d-block mt-quarter' });
        $btn.after($note);
      }

      $btn.prop('disabled', true);
      $note.text('');

      $.post(settings.ajax_url, {
        action:      'get_plants_in_bloom',
        ajax_nonce:  settings.nonce,
        exclude_date: $form.find('.acf-field[data-name="date_observed"] input').val() || '',
      }, function (response) {
        if (response && response.plants_in_bloom) {
          $textarea.val(response.plants_in_bloom).trigger('change');
          $note.text((a1.plants_found_note || 'Copied from observation on {date}.').replace('{date}', response.date_observed));
        } else {
          $note.text(a1.plants_not_found_note || 'No plants in bloom have been recorded for this site.');
        }
      }).always(function () { $btn.prop('disabled', false); });
    });

    // ── Data type radio and summary section ──────────────────────────────────
    const $data_type_field  = $form.find('.acf-field[data-name="activity_1_data_type"]');
    const $intent_field     = $form.find('.acf-field[data-name="submission_intent"]');
    const $intent_input     = $intent_field.find('input');
    const $native_submit    = $form.find('[type="submit"]');

    $native_submit.hide();

    function set_intent_and_submit(intent) {
      $intent_input.val(intent).trigger('change');
      $native_submit.trigger('click');
    }

    function get_selected_type() {
      return $data_type_field.find('input[type="radio"]:checked').val() || '';
    }

    // ── Edit mode: handle data type radio and section display ────────────────
    if (is_edit) {
      $data_type_field.hide();

      // Re-trigger conditional logic so the accordion reflects the prefilled value.
      const $checked_radio = $data_type_field.find('input[type="radio"]:checked');
      if ($checked_radio.length) {
        $checked_radio.trigger('change');
      }

      if (rec_type === 'detail') {
        $data_type_field.closest('.acf-accordion').hide();
        // ACF hides the summary accordion via acf-hidden for detail type;
        // remove that class so it's visible (read-only) in edit mode. Its
        // inputs stay disabled (not posted, not validated) — for detail
        // records these values are derived from plant rows, and the server
        // leaves them out of the save and rebuilds them
        // (Activity_1::get_detail_derived_field_names()).
        const $summary_accordion = $form.find('#summary-section-label').closest('.acf-accordion');
        $summary_accordion.removeClass('acf-hidden').css('display', 'block');
        make_summary_section_readonly($summary_accordion);
        add_detail_data_link($form, ctx.date_summary_url, a1.detail_record_notice, a1.detail_record_link_text);
      }

      if (rec_type === 'summary' && ctx.convert_url) {
        add_convert_notice($form, a1.convert_warning, a1.btn_convert, ctx.convert_url);
      }
    }

    // ── Custom buttons ───────────────────────────────────────────────────────
    const $btn_wrap = $('<div>', { class: 'form-actions a1-form-actions' }).insertAfter($form);

    // Cancel navigates directly — never submits — so validation cannot block it.
    const cancel_url = ctx.cancel_url || '/';

    function cancel() { window.location.href = cancel_url; }

    function render_buttons() {
      $btn_wrap.empty();

      const selected = get_selected_type();

      if (is_edit) {
        // Edit mode — always Cancel + Save regardless of record type.
        add_btn($btn_wrap, 'secondary', a1.btn_cancel || 'Cancel', cancel);
        add_btn($btn_wrap, 'primary', a1.btn_save || 'Save', function () {
          set_intent_and_submit('save');
        });

      } else if (selected === 'summary') {
        // New + summary selected.
        add_btn($btn_wrap, 'secondary', a1.btn_cancel || 'Cancel', cancel);
        add_btn($btn_wrap, 'primary', a1.btn_save_more || 'Save and Add Another Monitoring Date', function () {
          set_intent_and_submit('summary_add_more');
        });
        add_btn($btn_wrap, 'primary', a1.btn_save_done || 'Save and Done', function () {
          set_intent_and_submit('summary');
        });

      } else if (selected === 'detail') {
        // New + detail selected.
        add_btn($btn_wrap, 'secondary', a1.btn_cancel || 'Cancel', cancel);
        add_btn($btn_wrap, 'primary', a1.btn_save_add_plants || 'Save and Add Plants', function () {
          set_intent_and_submit('detail');
        });

      } else {
        // New + no type selected yet — Cancel only.
        add_btn($btn_wrap, 'secondary', a1.btn_cancel || 'Cancel', cancel);
      }
    }

    // Re-render buttons when the data type selection changes (new record only).
    if (!is_edit) {
      $data_type_field.on('change', 'input[type="radio"]', render_buttons);
    }

    render_buttons();

    // ── Milkweed species: warn when a selection is not in site description ───
    const site_species = ctx.site_species || [];
    if (site_species.length > 0) {
      const $mw_field   = $form.find('.acf-field[data-name="mw_species"]');
      const $mw_warning = $('<div>', { class: 'mlmp-notice mlmp-notice-info a1-mw-species-warning mt-quarter' }).hide();
      $mw_field.append($mw_warning);

      function check_mw_species_warning() {
        const selected = $mw_field.find('select').val() || [];
        const new_species = selected.filter(function (s) { return site_species.indexOf(s) === -1; });
        if (new_species.length > 0) {
          $mw_warning.html(a1.mw_species_new_warning || 'One or more selected species are not in your site description. Saving will automatically add them to your site\'s species list.').show();
        } else {
          $mw_warning.hide();
        }
      }

      $mw_field.on('change', check_mw_species_warning);
      check_mw_species_warning();
    }

  }); // end acf ready

  // ── Helpers ─────────────────────────────────────────────────────────────────

  function add_btn($container, style, label, on_click) {
    const css = 'mlmp-button-small';
    $('<button>', { type: 'button', class: css, text: label })
      .on('click', on_click)
      .appendTo($container);
  }

  /**
   * Makes the detail record's summary section visually read-only (its
   * values are derived from plant rows).
   *
   * @param {jQuery} $accordion The summary accordion element.
   * @returns {void}
   */
  function make_summary_section_readonly($accordion) {
    $accordion.find('.acf-field').each(function () {
      $(this).css({ opacity: '0.6', 'pointer-events': 'none' });
      $(this).find('input, textarea').attr('readonly', true);
    });
  }



  function add_detail_data_link($form, date_summary_url, notice_tmpl, link_text) {
    if (!date_summary_url) return;
    const $link = $('<a>', { href: date_summary_url, text: link_text || 'View and edit plant records' });
    const tmpl = notice_tmpl || 'This is a <strong>Detail (1C)</strong> record. Summary totals are calculated from individual plant entries. {link}.';
    const $notice = $('<div>', { class: 'mlmp-notice mlmp-notice-info a1-detail-data-notice mt-1 mb-1' });
    $notice.html(tmpl.replace('{link}', $link[0].outerHTML));
    $form.find('.acf-field[data-name="alive"]').closest('.acf-accordion, .acf-fields').before($notice);
  }

  function add_convert_notice($form, warning_text, btn_label, convert_url) {
    const text = warning_text ||
      '<i class="fas fa-info-circle"></i> <strong>Need to switch from Activity 1 Summary to Activity 1 Detail data?</strong><br>' +
      'Click the button below to start entering Activity 1 Detail (individual plant) data for this date. ' +
      'Once you enter Activity 1 Detail plants, your Activity 1 Summary data will be replaced with automatically calculated totals.';
    const $notice = $('<div>', { class: 'mlmp-notice mlmp-notice-info a1-convert-notice mt-1 mb-1' });
    $notice.append($('<p>').html(text));
    $notice.append(
      $('<button>', { type: 'button', class: 'mlmp-button-small-inv', text: btn_label || 'Switch to Activity 1 Detail (individual plant) data' })
        .on('click', function () { window.location.href = convert_url; })
    );
    $form.find('.acf-field[data-name="activity_1_data_type"]').after($notice);
  }
};
